import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, isObservable } from 'rxjs';
import Swal from 'sweetalert2';

export interface GlobalModalConfig {
  title: string;
  subtitle?: string;
  icon?: string;
  iconClass?: string;
  message?: string;
  points?: string[];
  subMessage?: string;
  highlightLabel?: string;
  highlightItems?: (string | number)[];
  confirmText?: string;
  cancelText?: string;
  confirmBtnClass?: string;
  cancelBtnClass?: string;
  hideCancel?: boolean;
  confirmDisabled?: boolean;
  align?: 'center' | 'left';
  onConfirm?: () => Observable<unknown> | Promise<unknown> | void;
  successTitle?: string;
  successMessage?: string;
  errorTitle?: string;
  showFeedback?: boolean;
}

@Component({
  selector: 'app-global-modal',
  standalone: true,
  imports: [
    CommonModule,
  ],
  templateUrl: './global-modal.component.html',
  styleUrls: [
    './global-modal.component.scss',
  ],
})
export class GlobalModalComponent {
  @Input({ required: true }) config!: GlobalModalConfig;

  public loading = false;

  constructor(public activeModal: NgbActiveModal) {}

  get confirmText(): string {
    return this.config?.confirmText || 'Confirm';
  }

  get cancelText(): string {
    return this.config?.cancelText || 'Cancel';
  }

  get confirmBtnClass(): string {
    return this.config?.confirmBtnClass || 'btn-primary';
  }

  get cancelBtnClass(): string {
    return this.config?.cancelBtnClass || 'btn-outline-secondary';
  }

  public cancel(): void {
    if (this.loading) {
      return;
    }
    this.activeModal.dismiss('cancel');
  }

  public confirm(): void {
    if (this.loading) {
      return;
    }

    const result = this.config?.onConfirm?.();

    if (result === undefined || result === null) {
      this.activeModal.close('confirm');
      return;
    }

    this.loading = true;

    if (isObservable(result)) {
      result.subscribe({
        next: () => this.handleSuccess(),
        error: (err) => this.handleError(err),
      });
      return;
    }

    Promise.resolve(result as Promise<unknown>).then(
      () => this.handleSuccess(),
      (err) => this.handleError(err),
    );
  }

  private handleSuccess(): void {
    this.loading = false;
    this.activeModal.close('confirm');

    if (this.config?.showFeedback === false) {
      return;
    }

    Swal.fire({
      title: this.config?.successTitle || 'Success',
      text: this.config?.successMessage || '',
      icon: 'success',
      confirmButtonText: 'OK',
    });
  }

  private handleError(err: unknown): void {
    this.loading = false;

    if (this.config?.showFeedback === false) {
      return;
    }

    const message = (err as { error?: { message?: string } })?.error?.message || 'Something went wrong. Please try again.';

    Swal.fire({
      title: this.config?.errorTitle || 'Action Failed',
      text: message,
      icon: 'error',
      confirmButtonText: 'OK',
      customClass: { container: 'swal-on-top' },
    });
  }
}
